import { includes } from 'libraries/lodash'
import getUrl from 'libraries/front-end-core/core/url'

import { CURRENT_AWARENESS, LEGISLATION, LEGISLATION_REVIEW, QA_CONTENT, QA_MENU, QA_REVIEW } from 'constants/controllers'
import { searchResultExpandCallback as caExpandCallback } from 'services/CurrentAwarenessService'
import { expandCallback as qaExpandCallback } from 'services/QAService'
import { expandCallback as legExpandCallback } from 'services/LegislationService'
import {
  SET_ACCORDION_EXPANDED_NODE_ID,
  SET_ACCORDION_EXPANDED_NODE_TITLE,
  SET_ACCORDION_HIGHLIGHT,
  SET_ACCORDION_GROUP,
  SET_ACCORDION_EVENT_CATEGORY,
  SET_ACCORDION_HAS_EXPAND_CALLBACK,
  SET_ACCORDION_BREADCRUMB_CLICKED,
  SET_ACCORDION_SECTION_ID,
  SET_ACCORDION_URL_NODE,
  SET_ACCORDION_TEXT_FRAGMENT
} from './types'
import { AccordionState } from 'redux/reducers/accordion'
import { AccordionGroup, ExpandCallback, TextFragment } from 'interfaces/accordion'
import { AppDispatch, RootState } from 'redux/store'

const tocIdMap = new Map() // tocId -> <groupId>

const updateTocIdMapFromGroup = (group: AccordionGroup[]): void => {
  for (let i = 0; i < group.length; i++) {
    if (group[i].tocIds === undefined) continue
    for (let j = 0; j < group[i].tocIds.length; j++) {
      if (!tocIdMap.has(group[i].tocIds[j])) {
        tocIdMap.set(group[i].tocIds[j], group[i].id)
      }
    }
  }
}

export const setAccordionExpandedNodeId = (expandedNodeId: string | null): { type: typeof SET_ACCORDION_EXPANDED_NODE_ID, expandedNodeId: string | null } => ({
  type: SET_ACCORDION_EXPANDED_NODE_ID,
  expandedNodeId
})

export const setAccordionExpandedNodeTitle = (expandedNodeTitle: string | null): { type: typeof SET_ACCORDION_EXPANDED_NODE_TITLE, expandedNodeTitle: string | null } => ({
  type: SET_ACCORDION_EXPANDED_NODE_TITLE,
  expandedNodeTitle
})

export const setAccordionHighlight = (highlight: string): { type: typeof SET_ACCORDION_HIGHLIGHT, highlight: string } => ({
  type: SET_ACCORDION_HIGHLIGHT,
  highlight
})

export const setAccordionGroup = (group: number | AccordionGroup[]): { type: typeof SET_ACCORDION_GROUP, group: number | AccordionGroup[] } => ({
  type: SET_ACCORDION_GROUP,
  group
})

export const setAccordionHasExpandCallback = (hasExpandCallback: boolean): { type: typeof SET_ACCORDION_HAS_EXPAND_CALLBACK, hasExpandCallback: boolean } => ({
  type: SET_ACCORDION_HAS_EXPAND_CALLBACK,
  hasExpandCallback
})

export const setAccordionEventCategory = (eventCategory: string): { type: typeof SET_ACCORDION_EVENT_CATEGORY, eventCategory: string } => ({
  type: SET_ACCORDION_EVENT_CATEGORY,
  eventCategory
})

export const setAccordionBreadCrumbClicked = (breadCrumbClicked: boolean): { type: typeof SET_ACCORDION_BREADCRUMB_CLICKED, breadCrumbClicked: boolean } => ({
  type: SET_ACCORDION_BREADCRUMB_CLICKED,
  breadCrumbClicked
})

export const setAccordionSectionId = (sectionId: string): { type: typeof SET_ACCORDION_SECTION_ID, sectionId: string } => ({
  type: SET_ACCORDION_SECTION_ID,
  sectionId
})

export const setAccordionUrlNode = (urlNode: string): { type: typeof SET_ACCORDION_URL_NODE, urlNode: string } => ({
  type: SET_ACCORDION_URL_NODE,
  urlNode
})

export const setAccordionTextFragment = (textFragment: TextFragment | null): { type: typeof SET_ACCORDION_TEXT_FRAGMENT, textFragment: TextFragment | null } => ({
  type: SET_ACCORDION_TEXT_FRAGMENT,
  textFragment
})

export type SetAccordionStateProps = Partial<AccordionState> & ExpandCallback & { aol?: string }
export const setAccordionState = ({ highlight, eventCategory, group, expandCallback, sectionId, aol }: SetAccordionStateProps): (dispatch: AppDispatch, getState: () => RootState) => void => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setAccordionEventCategory(eventCategory ?? ''))
    dispatch(setAccordionHasExpandCallback(true))
    dispatch(setAccordionHighlight(highlight ?? ''))
    dispatch(setAccordionGroup(group ?? []))
    dispatch(setAccordionSectionId(sectionId ?? ''))

    const {
      layout: { flavour, controller, pageData: { fromDate, toDate } },
      accordion: { expandedNodeId, breadCrumbClicked }
    } = getState()

    const highlightPathItems = highlight?.split('/')
    const highlightId = highlightPathItems?.[highlightPathItems.length - 1]

    let url
    switch (controller) {
      case CURRENT_AWARENESS:
        url = getUrl(CURRENT_AWARENESS, { fromDate, toDate })
        break
      case LEGISLATION:
        url = getUrl(LEGISLATION, { aol, sectionId, focusedId: highlightId })
        break
      case QA_CONTENT:
        url = getUrl(QA_CONTENT, { aol, flavour, sectionId, focusedId: highlightId })
        break
      case QA_MENU:
        url = getUrl(QA_CONTENT, { aol, flavour, sectionId, focusedId: highlightId })
        break
      case QA_REVIEW:
      case LEGISLATION_REVIEW:
        break
      default:
        console.error('unknown controller in setAccordionState:', controller)
        break
    }

    dispatch(setAccordionUrlNode(url ?? ''))

    if (eventCategory === 'Q&A' && typeof group !== 'number' && Array.isArray(group) && !breadCrumbClicked) {
      const groupFound = group.find((item: { id: string, sectionIds: string[] }) =>
        // Solution to FC-1484, Links to S1 sections should expand to correct path
        ('#'.concat(item.id) === highlight) ||
        // Solution to FC-1542, Links to S2 sections should expand to correct path
        (item.sectionIds?.some((sectionId: string) => sectionId === highlightId))
      )

      if (groupFound !== undefined) {
        const path = groupFound.path.join('/')
        if ((expandCallback(path, 'group', flavour, 1) as boolean)) {
          if (expandedNodeId !== groupFound.id) {
            dispatch(setAccordionExpandedNodeId(groupFound.id))
          }
        }
      } else if (highlight !== undefined && highlight !== '') {
        // Solution to FC-1031, clicking back after clicking through question links
        if (!tocIdMap.has(highlightId)) {
          updateTocIdMapFromGroup(group)
        }

        const groupId = tocIdMap.get(highlightId)
        if (groupId !== undefined && groupId !== expandedNodeId) {
          dispatch(setAccordionExpandedNodeId(groupId))
        }
      }
    }
  }
}

export const updateExpandedNodeIdState = (id: string, value: string): (dispatch: AppDispatch, getState: () => RootState) => void => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const { group } = getState().accordion
    const idList = (typeof group !== 'number' && Array.isArray(group)) ? group.map((node: { id: any }) => node.id) : []

    if (+value !== 0) {
      if (includes(idList, id)) {
        dispatch(setAccordionExpandedNodeId(id))
      }
    } else {
      if (includes(idList, id)) {
        dispatch(setAccordionExpandedNodeId(null))
        dispatch(setAccordionExpandedNodeTitle(null))
      }
    }
  }
}

export const handleExpandedTitleOnClick = () => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const { expandedNodeId, hasExpandCallback, eventCategory, group } = getState().accordion
    const path = (Array.isArray(group) ? group : []).find((node: { id: string }) => node.id === expandedNodeId)?.path.join('/') ?? ''

    if (hasExpandCallback) {
      switch (eventCategory) {
        case 'CA':
          caExpandCallback('expand', 'group', path, 'false')
          break
        case 'Legislation':
          legExpandCallback('expand', 'group', path, 'false')
          break
        case 'Q&A':
          qaExpandCallback('expand', 'group', path, 'false')
          break
        default:
          console.error('eventCategory not recognised')
          break
      }
    }
    dispatch(setAccordionBreadCrumbClicked(true))
    dispatch(setAccordionExpandedNodeId(null))
    dispatch(setAccordionExpandedNodeTitle(null))
  }
}
